import React, { useState, useEffect } from "react";
import { WizardFormField } from "../machines/immersive-wizard.machine";
import { WizardFormData } from "../types/wizard.types";

interface SubdomainStepProps {
  formData: WizardFormData;
  onFormUpdate: (data: Partial<WizardFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

const SubdomainStep: React.FC<SubdomainStepProps> = ({
  formData,
  onFormUpdate,
  onValidationChange,
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [showMinLengthError, setShowMinLengthError] = useState(false);

  useEffect(() => {
    const validateSubdomain = (value: string) => {
      const newErrors = [];

      if (!value.trim()) {
        newErrors.push("Subdomain is required");
      } else if (value.length < 3 && showMinLengthError) {
        newErrors.push("Subdomain must be at least 3 characters");
      } else if (!/^[a-zA-Z0-9-]+$/.test(value)) {
        newErrors.push(
          "Subdomain can only contain letters, numbers, and hyphens",
        );
      } else if (value.startsWith("-") || value.endsWith("-")) {
        newErrors.push("Subdomain cannot start or end with a hyphen");
      }

      setErrors(newErrors);
      const isValid = newErrors.length === 0;
      onValidationChange(isValid);

      return isValid;
    };

    const subdomain = formData[WizardFormField.Subdomain];
    validateSubdomain(subdomain);
  }, [formData, onValidationChange, showMinLengthError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-zA-Z0-9-]/g, "");
    if (value.length <= 20) {
      onFormUpdate({
        [WizardFormField.Subdomain]: value,
      });
    }
  };

  const handleBlur = () => {
    setShowMinLengthError(true);
  };

  const subdomain = formData[WizardFormField.Subdomain];
  const hasErrors = errors.length > 0;

  return (
    <div>
      <label
        htmlFor="subdomain-input"
        className="tw-block tw-text-sm tw-font-medium tw-mb-2"
      >
        Choose your subdomain
      </label>
      <div
        className={`tw-flex tw-items-center tw-bg-neutral-800/10 tw-rounded-lg tw-p-3 tw-transition-colors ${
          hasErrors && subdomain ? "tw-ring-2 tw-ring-red-300" : ""
        }`}
      >
        <input
          id="subdomain-input"
          type="text"
          value={subdomain}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder="yoursite"
          className="tw-bg-transparent tw-text-neutral-800 tw-placeholder-neutral-800/60 tw-border-none tw-outline-none tw-flex-1"
        />
        <span className="tw-text-neutral-800/80 tw-ml-2">.pushpress.com</span>
      </div>

      {/* Character Counter */}
      {subdomain && (
        <div className="tw-mt-1 tw-text-right">
          <span
            className={`tw-text-xs ${subdomain.length >= 18 ? "tw-text-orange-500" : "tw-text-neutral-500"}`}
          >
            {subdomain.length}/20
          </span>
        </div>
      )}

      {/* Error Messages */}
      {hasErrors && subdomain && (
        <div className="tw-mt-2">
          {errors.map((error, index) => (
            <p key={index} className="tw-text-red-500 tw-text-sm">
              {error}
            </p>
          ))}
        </div>
      )}

      {/* Helper Text */}
      {!subdomain && (
        <p className="tw-text-neutral-600 tw-text-sm tw-mt-2">
          This will be your unique business URL
        </p>
      )}
    </div>
  );
};

export default SubdomainStep;
